import NiceModal from "@ebay/nice-modal-react";
import { Col, Form, Input, Row, Tooltip } from "antd";
import dayjs from "dayjs";
import { useRef, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  AddButton,
  Amount,
  CoinWalletModalForm,
  CoinWalletStatus,
  CoinWalletViewMoreModal,
  ColumnType,
  CustomerStatistics,
  CustomerStatisticsHandler,
  DatePicker,
  DateTime,
  EditDropdown,
  PageContainer,
  PageHeader,
  SearchButton,
  Table,
  ViewMoreButton,
} from "src/components";
import { CheckOutlined } from "@ant-design/icons";
import { useCoinWallets, UseCoinWalletsFilters, useRevokeCoinWallet } from "src/hooks";
import { authInfoState, authState, modalState } from "src/recoils";
import { CoinWallet, ProductCode, Scope } from "src/types";
import { formatDatetime } from "src/utils";
import { colors } from "src/constants";
import slotLogo from "src/assets/logo/slot.svg";
import betLogo from "src/assets/logo/bet.svg";
import sportLogo from "src/assets/logo/sport.svg";

// Each bonus shows which products it applies to (API `on_products`) as 3 narrow
// "channel" columns: a vertical brand-logo header + a check mark when active.
const PRODUCT_COLUMNS: { code: ProductCode; logo: string; alt: string }[] = [
  { code: ProductCode.PIG_SPIN, logo: slotLogo, alt: "SLOT" },
  { code: ProductCode.PIG_CASINO, logo: betLogo, alt: "BET" },
  { code: ProductCode.PIG_SPORT, logo: sportLogo, alt: "SPORT" },
];

// Empty/undefined on_products = product unspecified. Default it to PIG_SPIN only,
// but flag it (yellow check + tooltip) so ops can tell it apart from an explicit SLOT-only bonus.
function getProductCheck(wallet: CoinWallet, code: ProductCode): { checked: boolean; unspecified: boolean } {
  const products = wallet.onProducts ?? [];
  if (products.length === 0) {
    return { checked: code === ProductCode.PIG_SPIN, unspecified: true };
  }
  return { checked: products.includes(code), unspecified: false };
}

// vertical (rotated) wordmark header — absolutely centred so the column stays narrow
function VerticalLogo({ logo, alt }: { logo: string; alt: string }) {
  return (
    <div style={{ position: "relative", width: 16, height: 56, margin: "0 auto" }}>
      <img
        src={logo}
        alt={alt}
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          height: 12,
          transform: "translate(-50%, -50%) rotate(-90deg)",
        }}
      />
    </div>
  );
}

function CustomerBonus() {
  const [form] = Form.useForm();
  const { scope } = useRecoilValue(authInfoState);
  const [auth] = useRecoilState(authState);
  const [, setModal] = useRecoilState(modalState);
  const customerStatistics = useRef<CustomerStatisticsHandler>(null);

  const [filters, setFilters] = useState<UseCoinWalletsFilters>();

  const { data, refetch, remove, isFetching, isError } = useCoinWallets(filters, {
    enabled: !!filters,
    keepPreviousData: true,
  });

  const { mutate: revoke } = useRevokeCoinWallet();

  const onFinish = (values: any) => {
    customerStatistics.current?.refetchBalance();
    remove();
    setFilters({
      start: formatDatetime(values.datetimeRange[0]),
      end: formatDatetime(values.datetimeRange[1]),
      customerCode: values.customerCode,
    });
  };

  const columns: ColumnType<CoinWallet>[] = [
    {
      title: "วันเวลาได้โบนัส",
      dataIndex: "createdAt",
      width: 100,
      render: (value) => <DateTime value={value} />,
    },
    {
      title: "โปรโมชั่น",
      dataIndex: "name",
      width: 100,
    },
    ...PRODUCT_COLUMNS.map<ColumnType<CoinWallet>>((product) => ({
      title: <VerticalLogo logo={product.logo} alt={product.alt} />,
      key: `product-${product.code}`,
      align: "center",
      width: 48,
      render: (_, record) => {
        const { checked, unspecified } = getProductCheck(record, product.code);
        if (!checked) return null;
        const icon = <CheckOutlined style={{ color: unspecified ? colors.yellow7 : colors.green6 }} />;
        return unspecified ? (
          <Tooltip
            title={
              <>
                โบนัสนี้ไม่ระบุ product
                <br />
                <i>default: <b>PIG_SLOT</b></i>
              </>
            }
          >
            {icon}
          </Tooltip>
        ) : (
          icon
        );
      },
    })),
    {
      title: "เติมเงิน (THB)",
      dataIndex: ["cashWalletTransaction", "transactionAmount"],
      render: (value) => <Amount value={value ? value : 0} />,
    },
    {
      title: "โบนัส (THB)",
      dataIndex: "initialBalance",
      render: (value) => <Amount value={value} />,
    },
    {
      title: "เทิร์นที่ต้องทำ (THB)",
      dataIndex: "totalTurnover",
      render: (value) => <Amount value={value} />,
    },
    {
      title: "เทิร์นที่ทำแล้ว (THB)",
      dataIndex: "currentTurnover",
      render: (value) => <Amount value={value} />,
    },
    {
      title: "โบนัสคงเหลือ (THB)",
      dataIndex: "currentBalance",
      render: (value) => <Amount value={value} />,
    },
    {
      title: "แปลงเป็นเงิน (THB)",
      dataIndex: "convertedToCashAmount",
      render: (value, record) => <Amount value={record.isConvertedToCash ? value : 0} />,
    },
    {
      title: "วันหมดอายุ",
      dataIndex: "expireAt",
      width: 100,
      render: (value) => <DateTime value={value} />,
    },
    {
      title: "สถานะ",
      key: "status",
      render: (_, record) => <CoinWalletStatus wallet={record} />,
    },
    {
      title: "วันเวลาอัพเดทล่าสุด",
      dataIndex: "updatedAt",
      width: 100,
      render: (value) => <DateTime value={value} />,
    },
    {
      key: "viewMore",
      align: "center",
      width: 40,
      render: (_, record) => (
        <ViewMoreButton
          size="small"
          onClick={() => {
            NiceModal.show(CoinWalletViewMoreModal, { walletUid: record.uid });
          }}
        />
      ),
    },
  ];

  const actionColumn: ColumnType<CoinWallet> = {
    key: "action",
    align: "center",
    width: 40,
    fixed: "right",
    render: (_, record) => {
      if (
        (auth.role === "DM" || auth.role === "Owner") &&
        ["active", "used"].includes(record.status)
      ) {
        return (
          <EditDropdown
            size="small"
            showEdit={true}
            showCancel={true}
            editText="แก้ไขโบนัส"
            cancelText="ยกเลิกโบนัส"
            onEdit={() =>
              NiceModal.show(CoinWalletModalForm, { wallet: record, onSuccess: () => refetch() })
            }
            onCancel={() => {
              setModal({
                isModalVisible: true,
                type: "warning",
                title: "ยกเลิกโบนัส",
                content: "ต้องการยกเลิกโบนัสใช่หรือไม่",
                buttonType: "question",
                onConfirm: async () => revoke(record.uid, { onSuccess: () => refetch() }),
              });
            }}
          />
        );
      }
    },
  };

  return (
    <PageContainer
      header={
        <PageHeader
          title=" "
          extra={[scope[Scope.AddBonus]?.enable
            && data && filters?.customerCode && (
              <AddButton
                key="add"
                onClick={() =>
                  NiceModal.show(CoinWalletModalForm, {
                    customerCode: filters.customerCode,
                    onSuccess: () => refetch(),
                  })
                }
              >
                เพิ่มโบนัส
              </AddButton>
            ),
          ]}
        >
          <Form
            form={form}
            onFinish={onFinish}
            layout="vertical"
            initialValues={{
              group: 1,
              datetimeRange: [
                dayjs().hour(0).minute(0).second(0).millisecond(0),
                dayjs().hour(23).minute(59).second(59).millisecond(999),
              ],
            }}
          >
            <Row gutter={8} wrap={false}>
              <Col flex="auto">
                <Form.Item name="customerCode" label="รหัสหรือเบอร์โทร">
                  <Input size="large" placeholder="ใส่รหัสหรือเบอร์โทร" allowClear />
                </Form.Item>
              </Col>
              <Col>
                <Form.Item
                  name="datetimeRange"
                  label={
                    <Row style={{ width: "340px" }}>
                      <Col span={12}>{"วันเวลาเริ่มต้น"}</Col>
                      <Col span={12}>{"วันเวลาสิ้นสุด"}</Col>
                    </Row>
                  }
                >
                  <DatePicker.RangePicker
                    size="large"
                    allowClear={false}
                    format="DD/MM/YYYY HH:mm"
                    showTime={{ format: "HH:mm" }}
                    style={{ width: "340px" }}
                  />
                </Form.Item>
              </Col>
              <Col>
                <Form.Item
                  label={<></>}
                  shouldUpdate={(prevValue, curValue) =>
                    prevValue.customerCode !== curValue.customerCode
                  }
                >
                  {() => (
                    <SearchButton
                      size="large"
                      htmlType="submit"
                      disabled={!form.getFieldValue("customerCode")}
                    />
                  )}
                </Form.Item>
              </Col>
            </Row>
          </Form>

          {data && (
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={24}>
                <CustomerStatistics customerCode={filters?.customerCode || ''} ref={customerStatistics} />
              </Col>
            </Row>
          )}
        </PageHeader>
      }
    >
      <Table
        rowKey="uid"
        columns={scope[Scope.EditBonus]?.enable ? [...columns, actionColumn] : columns}
        dataSource={isError ? [] : data?.results}
        loading={{
          size: "large",
          spinning: isFetching,
          style: { maxHeight: "calc(100vh - 310px)" },
        }}
        pagination={{ total: data?.total }}
      />
    </PageContainer>
  );
}

export default CustomerBonus;
